import React, { useState, useRef, useEffect } from 'react'; // Added useEffect
import {
  Box, Button, Typography, Paper, Grid, TextField, CircularProgress, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Collapse, IconButton
} from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'; // Added for scroll indication
import { v4 as uuidv4 } from 'uuid';
import { API_BASE_URL } from './api';

// Helper to format device info
function formatDevice(device) {
  if (!device) return '-';
  return `${device.name || device.hardware || device.uuid || 'Unknown'} (${device.uuid || ''})`;
}

const ONVIFDiscoveryPanel = ({ apiKey, clientId, clientName, onError }) => {
  const [discovering, setDiscovering] = useState(false);
  const [devices, setDevices] = useState([]);
  const [totalFound, setTotalFound] = useState(null);
  const [error, setError] = useState('');
  const [correlationID, setCorrelationID] = useState('');
  const [timeout, setTimeoutVal] = useState(10); // seconds
  const websocketRef = useRef(null);
  const tableContainerRef = useRef(null); // Ref for the table container
  const [showScrollArrow, setShowScrollArrow] = useState(false); // New state for arrow visibility

  // Effect to check table scrollability and scroll position for arrow
  useEffect(() => {
    const tableElement = tableContainerRef.current;

    const handleScrollAndResize = () => {
      if (tableElement) {
        const isContentScrollable = tableElement.scrollHeight > tableElement.clientHeight;
        const isScrolledToTop = tableElement.scrollTop < 5; // Small threshold for being at the top
        setShowScrollArrow(isContentScrollable && isScrolledToTop);
      } else {
        setShowScrollArrow(false);
      }
    };

    // Initial check
    handleScrollAndResize();

    if (tableElement) {
      tableElement.addEventListener('scroll', handleScrollAndResize);
    }
    // Also re-check on window resize as it might affect layout and scrollability
    window.addEventListener('resize', handleScrollAndResize);

    return () => {
      if (tableElement) {
        tableElement.removeEventListener('scroll', handleScrollAndResize);
      }
      window.removeEventListener('resize', handleScrollAndResize);
    };
  }, [devices]); // Re-run when devices array changes (affects scrollHeight)

  // Start ONVIF Discovery
  const startDiscovery = async () => {
    setError('');
    setDevices([]);
    setTotalFound(null);
    const corrID = uuidv4();
    setCorrelationID(corrID);
    setDiscovering(true);

    // Fetch WebSocket ticket
    let ticket;
    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/ws-ticket`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${apiKey}` },
      });
      if (!resp.ok) throw new Error('Failed to get WebSocket ticket');
      const data = await resp.json();
      ticket = data.ticket;
    } catch (err) {
      setError('Failed to obtain WebSocket ticket: ' + err.message);
      setDiscovering(false);
      if (onError) onError('WebSocket ticket fetch failed: ' + err.message);
      return;
    }

    // Open WebSocket
    let wsScheme = 'ws';
    let wsHost = API_BASE_URL.split('//')[1]; // Get host and potential port from API_BASE_URL
    if (API_BASE_URL.startsWith('https')) {
      wsScheme = 'wss';
    }
    // If API_BASE_URL includes a path, remove it for WebSocket host
    if (wsHost.includes('/')) {
        wsHost = wsHost.substring(0, wsHost.indexOf('/'));
    }
    const wsUrl = `${wsScheme}://${wsHost}/ws/admin?ticket=${ticket}`;

    const ws = new window.WebSocket(wsUrl);
    websocketRef.current = ws;

    ws.onopen = () => {
      // Send ONVIF discovery trigger
      ws.send(JSON.stringify({
        action: 'admin_trigger_onvif_discovery',
        payload: {
          targetClientID: clientId,
          correlationID: corrID,
          discoveryTimeoutS: Number(timeout)
        }
      }));
    };

    ws.onmessage = (event) => {
      let message;
      try {
        message = JSON.parse(event.data);
      } catch (err) {
        setError('Failed to parse WebSocket message: ' + err.message);
        return;
      }
      if (message.type === 'onvif_device_delivery') {
        if (message.payload && message.payload.correlationID === corrID) {
          setDevices(prev => {
            // Avoid duplicates by UUID
            const exists = prev.some(d => d.device && d.device.uuid === message.payload.device.uuid);
            return exists ? prev : [...prev, message.payload];
          });
        }
      } else if (message.type === 'onvif_discovery_complete') {
        if (message.payload && message.payload.correlationID === corrID) {
          setTotalFound(message.payload.totalDevicesFound);
          setDiscovering(false);
          ws.close();
        }
      } else if (message.type === 'onvif_discovery_error') {
        if (message.payload && message.payload.correlationID === corrID) {
          setError(message.payload.error || 'Discovery error');
          setDiscovering(false);
          ws.close();
        }
      }
    };

    ws.onerror = (err) => {
      setError('WebSocket error');
      setDiscovering(false);
      if (onError) onError('WebSocket connection error');
    };
    ws.onclose = () => {
      websocketRef.current = null;
    };
  };

  const cancelDiscovery = () => {
    if (websocketRef.current) {
      websocketRef.current.close();
      websocketRef.current = null;
    }
    setDiscovering(false);
    setCorrelationID('');
  };

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <VideocamIcon color="primary" />
        <Typography variant="h6">ONVIF Discovery</Typography>
      </Box>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label="Discovery Timeout (seconds)"
            type="number"
            size="small"
            value={timeout}
            onChange={e => setTimeoutVal(e.target.value)}
            disabled={discovering}
            inputProps={{ min: 1, max: 120 }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Button
            variant="contained"
            color="primary"
            onClick={startDiscovery}
            disabled={discovering}
            fullWidth
          >
            {discovering ? 'Discovering...' : 'Start Discovery'}
          </Button>
        </Grid>
        {discovering && (
          <Grid item xs={12} sm={12} md={4}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<CancelIcon />}
              onClick={cancelDiscovery}
              fullWidth
            >
              Cancel
            </Button>
          </Grid>
        )}
      </Grid>
      <Box sx={{ mt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {discovering && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">
              {devices.length > 0
                ? `Discovery in progress... ${devices.length} device(s) found. New devices will appear below.`
                : 'Discovery in progress... Searching for devices.'}
            </Typography>
          </Box>
        )}
        {devices.length > 0 && (
          <Box sx={{ position: 'relative' }}> {/* Wrapper for TableContainer and Arrow */}
            <TableContainer ref={tableContainerRef} sx={{ maxHeight: 300, mb: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Device Name</TableCell>
                    <TableCell>IP(s)</TableCell>
                    <TableCell>Hardware</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Profiles</TableCell>
                    <TableCell>Device Types</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {devices.map((d, idx) => (
                    <TableRow key={d.device?.uuid || idx}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{d.device?.name || '-'}</TableCell>
                      <TableCell>{(d.device?.xAddrs || []).join(', ')}</TableCell>
                      <TableCell>{d.device?.hardware || '-'}</TableCell>
                      <TableCell>{d.device?.location || '-'}</TableCell>
                      <TableCell>{(d.device?.profiles || []).join(', ')}</TableCell>
                      <TableCell>{(d.device?.deviceTypes || []).join(', ')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {showScrollArrow && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '10px', // Adjusted position to be further down
                  left: '50%',
                  transform: 'translateX(-50%)',
                  color: 'text.secondary',
                  pointerEvents: 'none', // So it doesn't interfere with scrolling
                  animation: 'bounce 2s infinite',
                  '@keyframes bounce': {
                    '0%, 20%, 50%, 80%, 100%': {
                      transform: 'translateX(-50%) translateY(0)',
                    },
                    '40%': {
                      transform: 'translateX(-50%) translateY(-6px)',
                    },
                    '60%': {
                      transform: 'translateX(-50%) translateY(-3px)',
                    },
                  },
                }}
              >
                <ArrowDownwardIcon fontSize="small" />
              </Box>
            )}
          </Box>
        )}
        {totalFound !== null && (
          <Alert severity="success">Discovery complete. Total devices found: {totalFound}</Alert>
        )}
        {!discovering && devices.length === 0 && !error && (
          <Alert severity="info">No discovery in progress. Start a new ONVIF discovery to find devices.</Alert>
        )}
      </Box>
    </Paper>
  );
};

export default ONVIFDiscoveryPanel;
